/**
 * Cart bundle grouping + reconciliation.
 *
 * A poster added together with a frame / passepartout carries a shared hidden
 * line-item property `_bundle` (poster variant id) plus `_bundle_role`
 * (`main` for the poster, `addon` for frame/mat) — set in product-form.js.
 * Shopify still stores each as its own line item, so this module makes the
 * group behave like a real bundle:
 *
 *   1. GROUPING  — visually nest add-on rows directly under their poster row
 *                  (cart page <tr> and cart drawer <li>).
 *   2. QTY SYNC  — when the poster quantity changes, the add-ons follow.
 *   3. CASCADE   — when the poster is removed, its add-ons are removed too.
 *   4. SELF-HEAL — any inconsistent state (from older carts, race conditions,
 *                  inventory caps) is reconciled on load / on every update.
 *
 * Add-on rows can still be removed individually (frame/mat), but their quantity
 * is driven by the poster (their steppers are hidden via CSS).
 *
 * It plugs into the theme's own cart flow: the theme dispatches `cart:updated`
 * after every /cart/change.js, and re-renders via the Section Rendering API. We
 * do the same, reusing the section list from the live <cart-items>/<mini-cart>
 * elements so we never diverge from the theme or trigger a full page reload.
 */
(function () {
  const ANY = '[data-bundle][data-bundle-role]';

  /* ============================================================= *
   *  1. VISUAL GROUPING (DOM)                                      *
   * ============================================================= */
  function processParent(parent) {
    if (!parent) return;

    parent
      .querySelectorAll('.cart-bundle-main, .cart-bundle-addon, .cart-bundle-addon--last')
      .forEach((el) => el.classList.remove('cart-bundle-main', 'cart-bundle-addon', 'cart-bundle-addon--last'));

    const byKey = new Map();
    Array.from(parent.children).forEach((el) => {
      if (!el.getAttribute) return;
      const key = el.getAttribute('data-bundle');
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(el);
    });

    byKey.forEach((group) => {
      const main = group.find((el) => el.getAttribute('data-bundle-role') === 'main');
      const addons = group.filter((el) => el.getAttribute('data-bundle-role') !== 'main');
      if (!main || !addons.length) return; // leave lone add-ons as normal rows

      main.classList.add('cart-bundle-main');
      let ref = main;
      addons.forEach((addon) => {
        addon.classList.add('cart-bundle-addon');
        if (ref.nextSibling !== addon) parent.insertBefore(addon, ref.nextSibling);
        ref = addon;
      });
      ref.classList.add('cart-bundle-addon--last');
    });
  }

  function groupAll() {
    const parents = new Set();
    document.querySelectorAll(ANY).forEach((el) => parents.add(el.parentElement));
    parents.forEach(processParent);
  }

  let groupRaf = null;
  function scheduleGroup() {
    if (groupRaf) cancelAnimationFrame(groupRaf);
    groupRaf = requestAnimationFrame(() => {
      groupRaf = null;
      groupAll();
    });
  }

  /* ============================================================= *
   *  2. QUANTITY SYNC / REMOVAL CASCADE (Cart AJAX API)           *
   * ============================================================= */

  // Merge the section render lists from every live cart element so we cover the
  // cart page, the drawer, the icon bubbles and the footer regardless of page.
  function sectionsToRender() {
    const seen = new Set();
    const list = [];
    document.querySelectorAll('cart-items, mini-cart').forEach((el) => {
      if (typeof el.getSectionsToRender !== 'function') return;
      el.getSectionsToRender().forEach((s) => {
        if (!s || !s.section || !s.id) return;
        const sig = `${s.id}|${s.selector}|${s.section}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        list.push(s);
      });
    });
    if (list.length) return list;
    // Fallback: drawer + bubbles only.
    return [
      { id: 'mini-cart', section: document.getElementById('mini-cart') && 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
    ].filter((s) => s.section);
  }

  function extractSection(html, selector) {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function applySections(parsed) {
    if (!parsed || !parsed.sections) return;
    sectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) return;
      const target = element.querySelector(section.selector) || element;
      const html = parsed.sections[section.section];
      if (target && html != null) {
        const inner = extractSection(html, section.selector);
        if (inner != null) target.innerHTML = inner;
      }
    });

    const empty = parsed.item_count === 0;
    document.querySelectorAll('cart-items').forEach((el) => el.classList.toggle('is-empty', empty));
    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
  }

  // Work out which add-on line items are out of sync with their poster.
  function computeUpdates(cart) {
    const groups = new Map();
    (cart.items || []).forEach((it) => {
      const key = it.properties && it.properties._bundle;
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    });

    const updates = {};
    groups.forEach((items) => {
      const main = items.find((i) => i.properties && i.properties._bundle_role === 'main');
      const addons = items.filter((i) => !(i.properties && i.properties._bundle_role === 'main'));
      if (!main) {
        addons.forEach((a) => {
          updates[a.key] = 0; // poster gone -> drop the add-on
        });
        return;
      }
      addons.forEach((a) => {
        if (a.quantity !== main.quantity) updates[a.key] = main.quantity;
      });
    });
    return updates;
  }

  function setBusy(state) {
    document.querySelectorAll('#main-cart-items, .cart__items').forEach((el) => {
      el.classList.toggle('cart__items--disabled', state);
    });
  }

  let reconciling = false;
  let lastUpdatesSig = null; // guards against unsatisfiable updates (inventory caps)

  async function reconcile() {
    if (reconciling) return;
    // Nothing to do if the cart has no bundles rendered.
    if (!document.querySelector(ANY)) {
      lastUpdatesSig = null;
      return;
    }
    reconciling = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = computeUpdates(cart);
      const keys = Object.keys(updates);
      if (!keys.length) {
        lastUpdatesSig = null;
        return;
      }

      const sig = JSON.stringify(updates);
      if (sig === lastUpdatesSig) return; // already tried, cannot satisfy -> stop looping
      lastUpdatesSig = sig;

      setBusy(true);
      const names = [...new Set(sectionsToRender().map((s) => s.section).filter(Boolean))];
      const parsed = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: names, sections_url: window.location.pathname }),
      }).then((r) => r.json());

      applySections(parsed);
      groupAll();
    } catch (e) {
      /* self-heals on the next cart event / page load */
    } finally {
      setBusy(false);
      reconciling = false;
    }
  }

  let reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 250);
  }

  /* ============================================================= *
   *  Wiring                                                        *
   * ============================================================= */
  function observe(el) {
    if (!el || el._cartBundleObserved) return;
    el._cartBundleObserved = true;
    new MutationObserver(scheduleGroup).observe(el, { childList: true, subtree: true });
  }

  function init() {
    groupAll();
    scheduleReconcile();
    observe(document.getElementById('main-cart-items'));
    observe(document.getElementById('mini-cart'));
    document.querySelectorAll('cart-drawer, .mini-cart, #CartDrawer').forEach(observe);
  }

  document.addEventListener('cart:updated', () => {
    scheduleGroup();
    scheduleReconcile();
  });
  document.addEventListener('cartdrawer:opened', scheduleGroup);
  document.addEventListener('shopify:section:load', () => {
    scheduleGroup();
    scheduleReconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
